import { useState, useEffect, useMemo } from 'react'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import {
    exportMultiSheet,
    buildDailyProductionSheet,
    buildWTISheet,
    buildConcentrationSheet,
    buildTopWellsSheet,
    buildMonthlySheet,
    buildQuarterlySheet
} from '../utils/exportExcel'
import KPICard from '../components/KPICard'
import ProductionChart from '../components/ProductionChart'
import ParetoChart from '../components/ParetoChart'
import WellsTable from '../components/WellsTable'
import MonthlyQuarterlyView from '../components/MonthlyQuarterlyView'

const CACHE_DURATION_MS = 5 * 60 * 1000

function Dashboard() {
    const { theme } = useTheme()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [fromCache, setFromCache] = useState(false)
    const [viewMode, setViewMode] = useState('gross') // 'gross' or 'net'
    const [timeView, setTimeView] = useState('daily') // 'daily' or 'monthly'
    const [monthlyData, setMonthlyData] = useState(null)
    const [monthlyLoading, setMonthlyLoading] = useState(false)

    useEffect(() => {
        fetchData()
    }, [])

    const getCacheKey = () => 'ip_production_daily'

    const getFromCache = () => {
        try {
            const cached = localStorage.getItem(getCacheKey())
            if (!cached) return null
            const { data, timestamp } = JSON.parse(cached)
            if (Date.now() - timestamp < CACHE_DURATION_MS) return data
            localStorage.removeItem(getCacheKey())
            return null
        } catch { return null }
    }

    const saveToCache = (data) => {
        try {
            localStorage.setItem(getCacheKey(), JSON.stringify({ data, timestamp: Date.now() }))
        } catch {}
    }

    const fetchMonthly = async (forceRefresh = false) => {
        if (!forceRefresh) {
            try {
                const cached = localStorage.getItem('ip_production_monthly')
                if (cached) {
                    const { data: d, timestamp } = JSON.parse(cached)
                    if (Date.now() - timestamp < CACHE_DURATION_MS) {
                        setMonthlyData(d)
                        return
                    }
                }
            } catch {}
        }
        setMonthlyLoading(true)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/dashboard/monthly`)
            if (!res.ok) throw new Error('Failed to fetch monthly data')
            const json = await res.json()
            setMonthlyData(json)
            try { localStorage.setItem('ip_production_monthly', JSON.stringify({ data: json, timestamp: Date.now() })) } catch {}
        } catch (err) {
            setError(err.message)
        } finally {
            setMonthlyLoading(false)
        }
    }

    // Lazy load monthly data when user switches to that view
    useEffect(() => {
        if (timeView === 'monthly' && !monthlyData) {
            fetchMonthly()
        }
    }, [timeView])

    const fetchData = async (forceRefresh = false) => {
        setLoading(true)
        setError(null)
        setFromCache(false)

        if (!forceRefresh) {
            const cached = getFromCache()
            if (cached) {
                setData(cached)
                setLoading(false)
                setFromCache(true)
                return
            }
        }

        try {
            const res = await fetch(`${apiConfig.baseUrl}/dashboard`)
            if (!res.ok) throw new Error('Failed to fetch data')
            const json = await res.json()
            setData(json)
            saveToCache(json)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    if (error && !data) {
        return (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                <p className="font-semibold">Error loading data</p>
                <p className="text-sm mt-1">{error}</p>
                <button onClick={() => fetchData(true)}
                    className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                    Retry
                </button>
            </div>
        )
    }

    const kpis = data?.kpis
    const today = data?.today
    const prevW = data?.previousWeek
    const production = data?.production
    const wells = data?.wells
    const isNet = viewMode === 'net'

    // Pick gross or net values
    const oilPerDay = isNet ? kpis?.netOilPerDay : kpis?.grossOilPerDay
    const gasPerDay = isNet ? kpis?.netGasPerDay : kpis?.grossGasPerDay
    const prevOilPerDay = isNet ? prevW?.netOilPerDay : prevW?.grossOilPerDay
    const prevGasPerDay = isNet ? prevW?.netGasPerDay : prevW?.grossGasPerDay
    const todayOil = isNet ? today?.netOil : today?.grossOil
    const todayGas = isNet ? today?.netGas : today?.grossGas

    // WoW deltas
    const computeDelta = (current, previous) => {
        if (!previous || previous === 0 || !current) return null
        return ((current - previous) / previous) * 100
    }
    const oilDelta = computeDelta(oilPerDay, prevOilPerDay)
    const gasDelta = computeDelta(gasPerDay, prevGasPerDay)
    const uptimeDelta = kpis?.avgUptimePercent && prevW?.avgUptimePercent
        ? kpis.avgUptimePercent - prevW.avgUptimePercent
        : null

    const handleDownloadAll = () => {
        const dateStr = new Date().toISOString().slice(0, 10)
        const prefix = isNet ? 'Net' : 'Gross'

        if (timeView === 'monthly' && monthlyData) {
            const sheets = [
                buildMonthlySheet(monthlyData, prefix, isNet),
                buildQuarterlySheet(monthlyData, prefix, isNet)
            ].filter(Boolean)
            if (sheets.length) exportMultiSheet(sheets, `Production_Monthly_${dateStr}`)
        } else {
            const sheets = [
                buildDailyProductionSheet(production, isNet, prefix),
                buildWTISheet(data?.wtiPrice),
                buildConcentrationSheet(data?.concentration),
                buildTopWellsSheet(wells?.wells, isNet, prefix)
            ].filter(Boolean)
            if (sheets.length) exportMultiSheet(sheets, `Production_Data_${dateStr}`)
        }
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="min-w-0">
                    <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                        <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                        Production Dashboard
                    </h1>
                    <p className={`${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 sm:mt-2 ml-3 sm:ml-4`}>
                        Last updated: {kpis?.lastUpdated ? new Date(kpis.lastUpdated).toLocaleString() : 'N/A'}
                        {fromCache && <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-blue-400 bg-blue-950' : 'text-blue-700 bg-blue-100'} px-2 py-0.5 rounded`}>(cached)</span>}
                    </p>
                </div>

                {/* Daily/Monthly + Gross/Net Toggle + Download + Refresh */}
                <div className="flex items-center space-x-3 flex-shrink-0">
                    <div className={`inline-flex rounded-lg p-0.5 ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-gray-200 border border-gray-300'}`}>
                        <button
                            onClick={() => setTimeView('daily')}
                            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                timeView === 'daily'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : theme === 'dark'
                                        ? 'text-gray-400 hover:text-gray-200'
                                        : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Daily
                        </button>
                        <button
                            onClick={() => setTimeView('monthly')}
                            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                timeView === 'monthly'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : theme === 'dark'
                                        ? 'text-gray-400 hover:text-gray-200'
                                        : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Monthly
                        </button>
                    </div>
                    <div className={`inline-flex rounded-lg p-0.5 ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-gray-200 border border-gray-300'}`}>
                        <button
                            onClick={() => setViewMode('gross')}
                            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                viewMode === 'gross'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : theme === 'dark'
                                        ? 'text-gray-400 hover:text-gray-200'
                                        : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Gross
                        </button>
                        <button
                            onClick={() => setViewMode('net')}
                            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                viewMode === 'net'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : theme === 'dark'
                                        ? 'text-gray-400 hover:text-gray-200'
                                        : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Net
                        </button>
                    </div>
                    <button onClick={handleDownloadAll}
                        className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`} title="Download Excel">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                    </button>
                    <button onClick={() => fetchData(true)}
                        className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm rounded-lg transition ${
                            theme === 'dark'
                                ? 'bg-slate-900 text-gray-300 hover:bg-slate-800 border border-slate-800'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`} title="Refresh data">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {timeView === 'monthly' ? (
                <MonthlyQuarterlyView
                    data={monthlyData}
                    loading={monthlyLoading}
                    viewMode={viewMode}
                />
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <KPICard
                            title={`${isNet ? 'Net' : 'Gross'} Oil (Today)`}
                            value={todayOil?.toLocaleString() || '0'}
                            unit="BBL"
                            subtitle={`7-Day Avg: ${oilPerDay?.toLocaleString() || '0'} BBL/d`}
                            delta={oilDelta}
                        />
                        <KPICard
                            title={`${isNet ? 'Net' : 'Gross'} Gas (Today)`}
                            value={todayGas?.toLocaleString() || '0'}
                            unit="MCF"
                            subtitle={`7-Day Avg: ${gasPerDay?.toLocaleString() || '0'} MCF/d`}
                            delta={gasDelta}
                        />
                        <KPICard
                            title="Wells"
                            value={data?.wellClassification?.producing?.toLocaleString() || '0'}
                            unit="Producing"
                            subtitle={`${data?.wellClassification?.injecting || 0} Injecting`}
                        />
                        <KPICard
                            title="Avg Uptime"
                            value={kpis?.avgUptimePercent?.toFixed(1) || '0'}
                            unit="%"
                            subtitle="Operational efficiency"
                            delta={uptimeDelta}
                        />
                    </div>

                    {/* Production Chart */}
                    <div className="chart-container">
                        <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                            Daily {isNet ? 'Net' : 'Gross'} Production Trend
                        </h2>
                        <ProductionChart
                            actualData={production?.actual || []}
                            forecastData={production?.forecast || []}
                            viewMode={viewMode}
                        />
                    </div>

                    {/* WTI Price Chart */}
                    {data?.wtiPrice && (
                        <div className={`rounded-lg border p-4 ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    WTI Crude Oil (90 Day)
                                </span>
                                <span className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    ${data.wtiPrice.current?.toFixed(2) || '-'}/BBL
                                </span>
                            </div>
                            {data.wtiPrice.history?.length > 0 && (
                                <ResponsiveContainer width="100%" height={120}>
                                    <AreaChart data={data.wtiPrice.history} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                                        <defs>
                                            <linearGradient id="wtiGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#64748b' : '#94a3b8' }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={d => {
                                                const dt = new Date(d)
                                                return `${dt.getMonth() + 1}/${dt.getDate()}`
                                            }}
                                            interval={Math.floor((data.wtiPrice.history.length - 1) / 5)}
                                        />
                                        <YAxis
                                            domain={['dataMin - 2', 'dataMax + 2']}
                                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#64748b' : '#94a3b8' }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={v => `$${v}`}
                                            width={45}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: theme === 'dark' ? '#1e293b' : '#fff',
                                                border: `1px solid ${theme === 'dark' ? '#334155' : '#e2e8f0'}`,
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                            }}
                                            formatter={v => [`$${Number(v).toFixed(2)}/BBL`, 'WTI']}
                                            labelFormatter={d => new Date(d).toLocaleDateString()}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#22c55e"
                                            strokeWidth={2}
                                            fill="url(#wtiGrad)"
                                            dot={false}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    )}

                    {/* Production Concentration (Pareto) */}
                    {data?.concentration?.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                                Production Concentration
                            </h2>
                            <ParetoChart data={data.concentration} maxBars={50} />
                        </div>
                    )}

                    {/* Top Wells Table — display only, no well drill-down */}
                    <div className="chart-container">
                        <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                            Top Producing Wells (Last 30 Days)
                        </h2>
                        <WellsTable
                            wells={wells?.wells || []}
                            viewMode={viewMode}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default Dashboard
